<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import IconArrowRightWhite from '@icons/ph_arrow-right_white.svg';
import IconArrowRightGreyDark from '@icons/ph_arrow-right_gray-dark.svg';
import { useManagePayAccount } from '@stores/useManagePayAccount';
import { PhoneNumber } from '@tools/phoneNumber';
import { trans } from '@elements/translation';
import Typography from '@components/Typography.vue';
import { getPayAccountInvoicingValidator } from '@pages/pay/store/validators/payAccountInvoicingValidator';
import { useLoading } from '@tools/composables/useLoading';
import PhoneInput from '@components/PhoneInput.vue';
import Button from '@components/Button.vue';
import Icon from '@components/Icon.vue';

const { isLoading, startLoading, stopLoading } = useLoading();
const managePayAccount = useManagePayAccount();
managePayAccount.setPayOrganizationBillingInformation({
  ...window.initialState.payOrganizationBillingInformation,
  payOrganizationType: 'seller',
  payOrganizationId: window.initialState.payOrganizationSeller?.id,
  company_name: window.initialState.organization?.name,
});
const emit = defineEmits([ 'next-step' ]);

const { payOrganizationBillingInformation } = storeToRefs(managePayAccount);

const rules = computed(() => getPayAccountInvoicingValidator());
const v$ = useVuelidate(rules, payOrganizationBillingInformation.value);

const isValidPhone = ref(true);
const isPhoneTouched = ref(false);

const { calculateVATNumber } = managePayAccount;

const onSirenBlur = () => {
  v$.value.siren.$touch();

  if (v$.value.siren.$invalid) {
    return;
  }

  payOrganizationBillingInformation.value.vat_number = calculateVATNumber(payOrganizationBillingInformation.value.siren);
};

const phoneNumber = computed({
  get: () => new PhoneNumber(payOrganizationBillingInformation.value.phone_number ?? ''),
  set: (val) => {
    payOrganizationBillingInformation.value.phone_number = val.international;
    isPhoneTouched.value = true;
    isValidPhone.value = val.isValid || !val;
  },
});

const isNextDisabled = computed(() => v$.value.$invalid || !isValidPhone.value);

const handleClickValidate = async () => {
  v$.value.$touch();
  if (isNextDisabled.value) {
    return;
  }

  startLoading();
  if (!payOrganizationBillingInformation.value.phone_number || payOrganizationBillingInformation.value.phone_number === '+') {
    payOrganizationBillingInformation.value.phone_number = null;
  }

  await managePayAccount.createOrUpdatePayOrganizationClientBillingInformation();
  stopLoading();
  emit('next-step');
};
</script>

<template>
  <div class="manage manage-onboarding">
    <div class="manage-onboarding__title">
      {{ trans('gestion.pay.informations.legal_infos.check.title') }}
    </div>
    <div class="manage-onboarding__text">
      {{ trans('gestion.pay.informations.legal_infos.check.text', { company_name: $companyName }) }}
    </div>
    <div class="manage__form">
      <div
        v-for="field in [ 'legal_name', 'street_and_number', 'additional_info' ]"
        :key="field"
        class="manage__form-group"
      >
        <label class="manage__label">{{ trans(`gestion.pay.informations.${field}`) }}</label>
        <input
          v-model="payOrganizationBillingInformation[field]"
          type="text"
          class="manage__input"
          :class="{ error: v$[field].$error }"
          @blur="v$[field].$touch()"
        >
        <span v-if="v$[field].$error" class="error-message">{{ v$[field].$errors[0].$message }}</span>
      </div>

      <div class="manage__form-group-row">
        <div class="manage__form-group">
          <label class="manage__label">{{ trans('gestion.pay.informations.zip_code') }}</label>
          <input
            v-model="payOrganizationBillingInformation.zip_code"
            type="text"
            class="manage__input"
            :class="{ error: v$.zip_code.$error }"
            @blur="v$.zip_code.$touch()"
          >
          <span v-if="v$.zip_code.$error" class="error-message">{{ v$.zip_code.$errors[0].$message }}</span>
        </div>
        <div class="manage__form-group">
          <label class="manage__label">{{ trans('gestion.pay.informations.city') }}</label>
          <input
            v-model="payOrganizationBillingInformation.city"
            type="text"
            class="manage__input"
            :class="{ error: v$.city.$error }"
            @blur="v$.city.$touch()"
          >
          <span v-if="v$.city.$error" class="error-message">{{ v$.city.$errors[0].$message }}</span>
        </div>
      </div>

      <div v-for="field in [ 'siren', 'vat_number', 'email' ]" :key="field" class="manage__form-group">
        <label class="manage__label">{{ trans(`gestion.pay.informations.${field}`) }}</label>
        <input
          v-model="payOrganizationBillingInformation[field]"
          :type="field === 'email' ? 'email' : 'text'"
          class="manage__input"
          :class="{ error: v$[field].$error }"
          @blur="field === 'siren' ? onSirenBlur() : v$[field].$touch()"
        >
        <span v-if="v$[field].$error" class="error-message">{{ v$[field].$errors[0].$message }}</span>
      </div>

      <div class="manage__form-group">
        <label class="manage__label">{{ trans('gestion.pay.informations.phone') }}</label>
        <PhoneInput
          v-model="phoneNumber"
          class="manage__input"
          :class="{ error: !isValidPhone && isPhoneTouched }"
        />
        <span
          v-if="!isValidPhone && isPhoneTouched"
          class="error-message"
        >{{ trans('gestion.pay.informations.phone.invalid') }}</span>
      </div>
    </div>

    <Typography variant="body-small" color="gray-dark">
      {{ trans('gestion.subscription.general.mandatory_fields') }}
    </Typography>
    <div class="manage-onboarding__button-block">
      <Button
        class="btn btn--primary btn--jumbo"
        :class="{ 'loading-btn': isLoading }"
        :disabled="isNextDisabled || isLoading"
        @click="handleClickValidate"
      >
        {{ trans('global.pagination.next') }}

        <template v-if="!isLoading">
          <Icon v-if="!isNextDisabled" :svg="IconArrowRightWhite" />
          <Icon v-else :svg="IconArrowRightGreyDark" />
        </template>
      </Button>
    </div>
  </div>
</template>
